import { useId, useState, type FocusEventHandler } from 'react';
import { PhoneInput, type CountryIso2 } from 'react-international-phone';

import 'react-international-phone/style.css';
import './international-phone-input.css';

export interface InternationalPhoneInputProps {
  label?: string;
  value: string;
  onChange: (phone: string) => void;
  className?: string;
  error?: string;
  placeholder?: string;
  density?: 'default' | 'compact';
  id?: string;
  name?: string;
  autoComplete?: string;
  defaultCountry?: CountryIso2;
  disabled?: boolean;
  required?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
}

const PREFERRED_COUNTRIES: CountryIso2[] = ['ru', 'by', 'kz', 'ua', 'us', 'gb'];

function normalizedPhone(phone: string): string {
  if (!phone || phone.startsWith('+')) return phone;
  const digits = phone.replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

export function InternationalPhoneInput({
  label,
  value,
  onChange,
  className,
  error,
  placeholder = '999 000-00-00',
  density = 'default',
  id,
  name,
  autoComplete = 'tel',
  defaultCountry = 'ru',
  disabled = false,
  required = false,
  onBlur,
  onFocus,
}: InternationalPhoneInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const filled = normalizedPhone(value).length > 0;
  const visual = error
    ? { node: '186:39827', variant: 'error' }
    : focused && filled
      ? { node: '186:39807', variant: 'filled+focus' }
      : focused
        ? { node: '186:39789', variant: 'focus' }
        : filled
          ? { node: '186:39817', variant: 'filled' }
          : hovered
            ? { node: '186:39742', variant: 'hover' }
            : { node: '61:27165', variant: 'Default' };

  return (
    <div className={`lovo-phone-input-group lovo-phone-input-group--${density}${className ? ` ${className}` : ''}`}>
      <div
        className={`lovo-phone-input lovo-phone-input--${density}${error ? ' lovo-phone-input--error' : ''}`}
        data-figma-node={visual.node}
        data-figma-variant={`Property 1=${visual.variant}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="lovo-phone-input__field">
          {label ? <label className="lovo-phone-input__label" htmlFor={inputId}>{label}</label> : null}
          <PhoneInput
            className="lovo-phone-input__phone"
            countrySelectorStyleProps={{
              buttonClassName: 'lovo-phone-input__country-button',
              dropdownArrowClassName: 'lovo-phone-input__country-arrow',
              flagClassName: 'lovo-phone-input__flag',
              dropdownStyleProps: {
                className: 'lovo-phone-input__country-dropdown',
                listItemClassName: 'lovo-phone-input__country-option',
                listItemCountryNameClassName: 'lovo-phone-input__country-name',
                listItemDialCodeClassName: 'lovo-phone-input__country-code',
              },
            }}
            defaultCountry={defaultCountry}
            disabled={disabled}
            forceDialCode
            inputClassName="lovo-phone-input__control"
            inputProps={{
              'aria-describedby': error ? errorId : undefined,
              'aria-invalid': Boolean(error),
              autoComplete,
              id: inputId,
              inputMode: 'tel',
              name,
              required,
            }}
            placeholder={placeholder}
            preferredCountries={PREFERRED_COUNTRIES}
            value={normalizedPhone(value)}
            onBlur={(event) => {
              setFocused(false);
              onBlur?.(event);
            }}
            onChange={(phone) => onChange(phone)}
            onFocus={(event) => {
              setFocused(true);
              onFocus?.(event);
            }}
          />
        </div>
      </div>
      {error ? <span className="lovo-phone-input__error" id={errorId} role="alert">{error.replace(/\.+$/, '')}</span> : null}
    </div>
  );
}
